import { Tooltip } from '@mui/material';

import { classNames } from '../../../../../common/lib/classNames';
import { useContact } from '../../../../hooks/useContact';
import { WrappedLink } from '../../../WrappedLink';

const RADIUS = 13;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function creditScoreTone(score: number | null) {
  if (score === null) return { ring: `stroke-gray-300`, text: `text-gray-500`, bg: `bg-gray-50 border-gray-200` };
  if (score >= 80) return { ring: `stroke-green-600`, text: `text-green-700`, bg: `bg-green-50 border-green-200` };
  if (score >= 60) return { ring: `stroke-lime-600`, text: `text-lime-700`, bg: `bg-lime-50 border-lime-200` };
  if (score >= 40) return { ring: `stroke-amber-500`, text: `text-amber-700`, bg: `bg-amber-50 border-amber-200` };

  return { ring: `stroke-red-600`, text: `text-red-700`, bg: `bg-red-50 border-red-200` };
}

/** Score ring in the contact header. Clicking it opens the score settings */
export function ContactCreditScoreBadge() {
  const { data: contact } = useContact();

  if (!contact) return null;

  const score = contact.creditScore ?? null;
  const daysLate = contact.weightedAvgDaysLate ?? null;
  const colors = creditScoreTone(score);
  const dash = score === null ? 0 : (score / 100) * CIRCUMFERENCE;

  const tooltip = [
    score === null
      ? `No payment history and nothing outstanding yet`
      : `0-100 payment behaviour score. Starts at 100 and loses points for paying late and for the current overdue position`,
    daysLate === null
      ? null
      : `Pays ${Math.abs(Math.round(daysLate))} days ${daysLate > 0 ? `late` : `early`} on average, recent and larger payments count more`,
    `Click to change how the score is calculated`,
  ].filter(Boolean).join(`. `);

  const body = (
    <span
      className={ classNames(
        `inline-flex cursor-pointer items-center gap-2 rounded-full border py-0.5 pl-0.5 pr-3 transition hover:shadow-xs`,
        colors.bg,
      ) }
    >
      <span className={ `relative inline-flex h-8 w-8 items-center justify-center` }>
        <svg
          viewBox={ `0 0 32 32` }
          className={ `h-8 w-8 -rotate-90` }
        >
          <circle
            cx={ 16 }
            cy={ 16 }
            r={ RADIUS }
            className={ `fill-none stroke-black/10` }
            strokeWidth={ 3 }
          />
          <circle
            cx={ 16 }
            cy={ 16 }
            r={ RADIUS }
            className={ classNames(`fill-none`, colors.ring) }
            strokeWidth={ 3 }
            strokeLinecap={ `round` }
            strokeDasharray={ `${dash} ${CIRCUMFERENCE}` }
          />
        </svg>
        <span className={ classNames(`absolute text-[0.6875rem] font-semibold tabular-nums`, colors.text) }>
          { score ?? `–` }
        </span>
      </span>
      <span className={ `flex flex-col leading-tight` }>
        <span className={ `text-[0.625rem] uppercase tracking-wide text-gray-500` }>{ `Credit score` }</span>
        <span className={ classNames(`text-[0.8125rem] font-medium`, colors.text) }>
          { score === null ? `No data` : contact.creditScoreBand ?? score }
        </span>
      </span>
    </span>
  );

  return (
    <Tooltip title={ tooltip }>
      <WrappedLink to={ `/settings/general/advanced/credit-score` }>
        { body }
      </WrappedLink>
    </Tooltip>
  );
}
